import { Website } from '@betterfunnel/types';
import { ArrowLeftIcon } from '@heroicons/react/20/solid';
import {
  ArrowTopRightOnSquareIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  DeviceTabletIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import * as Sentry from '@sentry/react';
import { doc, onSnapshot } from 'firebase/firestore';
import { useContext, useEffect, useRef, useState } from 'react';
import Button from '../components/Common/Button';
import FullScreenModal from '../components/Common/FullScreenModal';
import Select from '../components/Common/Select';
import Toggle from '../components/Common/Toggle';
import DashboardAnalyticsRangeSelector from '../components/DashboardAnalyticsRangeSelector';
import { WebsitePreview } from '../components/WebsitePreview';
import { AnalyticsRange, DashboardContext } from '../contexts/DashboardContext';
import { DynamicPageContext } from '../contexts/DynamicPageContext';
import { UserContext } from '../contexts/UserContext';
import { getWebsiteOnViewAnalytics } from '../services/AnalyticsService';
import {
  PREVIEW_ROOT,
  analyticsRangeToDates,
  classNames,
  emptyConverter,
  sendMessageToIframe,
} from '../utils';

interface Props {
  open: boolean;
  onClose: () => void;
  websiteId: string;
  defaultShowAnalytics?: boolean;
}

export default function WebsitePreviewModal({
  defaultShowAnalytics,
  open,
  onClose,
  websiteId,
}: Props) {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const { selectedDynamicPage, analyticsRange } = useContext(DashboardContext);
  const { primaryDomain } = useContext(DynamicPageContext);

  const [device, setDevice] = useState('desktop');
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  const [analytics, setAnalytics] = useState<{
    websiteId: string;
    analyticsRange: AnalyticsRange;
    [key: string]: any;
  }>();
  const { selectedAccount } = useContext(UserContext);

  const [, setWebsite] = useState<Website>();

  useEffect(() => {
    if (open) return;

    setShowAnalytics(false);
  }, [open]);

  useEffect(() => {
    if (defaultShowAnalytics) setShowAnalytics(defaultShowAnalytics);
  }, [defaultShowAnalytics]);

  useEffect(() => {
    if (!selectedDynamicPage || !websiteId) return;

    const websiteRef = doc(
      selectedDynamicPage.ref,
      'websites',
      websiteId,
    ).withConverter(emptyConverter<Website>());
    const unsubscribe = onSnapshot(websiteRef, (website) => {
      if (!website.exists()) return;

      setWebsite(website.data());

      sendMessageToIframe({
        eventName: 'websiteUpdated',
        website: website.data().development,
      });
    });

    return () => {
      unsubscribe();
    };
  }, [websiteId, selectedDynamicPage]);

  useEffect(() => {
    if (!open || !showAnalytics) return;
    if (!selectedAccount || !selectedDynamicPage || !websiteId) return;

    if (
      analytics &&
      analytics.websiteId === websiteId &&
      analytics.analyticsRange === analyticsRange
    ) {
      return;
    }

    const { startDate, endDate } = analyticsRangeToDates(analyticsRange);
    setIsLoadingAnalytics(true);

    let relevant = true;
    getWebsiteOnViewAnalytics(
      selectedAccount.id,
      selectedDynamicPage.id,
      websiteId,
      startDate,
      endDate,
    )
      .then((res) => {
        if (!relevant) return;
        setAnalytics({ ...res, analyticsRange, websiteId });
      })
      .finally(() => {
        if (!relevant) return;
        setIsLoadingAnalytics(false);
      });

    return () => {
      relevant = false;
    };
  }, [
    websiteId,
    selectedAccount,
    selectedDynamicPage,
    open,
    showAnalytics,
    analyticsRange,
    analytics,
  ]);

  useEffect(() => {
    if (!showAnalytics || !open) {
      sendMessageToIframe({ eventName: 'hideAnalytics' }, iframeRef);
      return;
    }

    if (analytics) {
      sendMessageToIframe({ eventName: 'showAnalytics', analytics }, iframeRef);

      const handlePreviewEvent = (event: MessageEvent<string>) => {
        if (event.origin !== PREVIEW_ROOT) return;
        if (!selectedDynamicPage) return;

        try {
          const data = JSON.parse(event.data);
          if (data.eventName === 'ready') {
            sendMessageToIframe(
              { eventName: 'showAnalytics', analytics },
              iframeRef,
            );
          }
        } catch (e) {
          Sentry.captureException(e);
          console.error(e);
        }
      };
      window.addEventListener('message', handlePreviewEvent);

      return () => {
        window.removeEventListener('message', handlePreviewEvent);
      };
    }
  }, [showAnalytics, analytics, open, selectedDynamicPage]);

  return (
    <FullScreenModal open={open} onClose={() => {}}>
      <div className="h-full px-4 pb-4 pt-5 sm:p-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button
              variant="secondary"
              size="large"
              icon={
                <ArrowLeftIcon className="-ml-0.5 h-4 w-4" aria-hidden="true" />
              }
              onClick={() => onClose()}
            >
              Exit
            </Button>
            <Toggle
              label={'Show analytics'}
              checked={showAnalytics}
              loading={isLoadingAnalytics}
              onChange={(v) => setShowAnalytics(v)}
            />
          </div>
          <div className="flex items-stretch gap-4">
            <DashboardAnalyticsRangeSelector size="large" />
            <Select
              selected={device}
              onChange={(v) => setDevice(v)}
              options={[
                {
                  value: 'desktop',
                  child: (
                    <div className="inline-flex items-center gap-x-1.5 px-2">
                      <ComputerDesktopIcon
                        className="-ml-0.5 h-4 w-4"
                        aria-hidden="true"
                      />
                      Desktop
                    </div>
                  ),
                },
                {
                  value: 'mobile',
                  child: (
                    <div className="inline-flex items-center gap-x-1.5 px-2">
                      <DevicePhoneMobileIcon
                        className="-ml-0.5 h-4 w-4"
                        aria-hidden="true"
                      />
                      Mobile
                    </div>
                  ),
                },
                {
                  value: 'tablet',
                  child: (
                    <div className="inline-flex items-center gap-x-1.5 px-2">
                      <DeviceTabletIcon
                        className="-ml-0.5 h-4 w-4"
                        aria-hidden="true"
                      />
                      Tablet
                    </div>
                  ),
                },
              ]}
            />
            <Button
              variant="secondary"
              size="large"
              icon={
                <QuestionMarkCircleIcon
                  className="-ml-0.5 h-4 w-4"
                  aria-hidden="true"
                />
              }
              onClick={() => {
                window.open('https://docs.betterfunnel.ai', '_blank');
              }}
            ></Button>
            <Button
              variant="secondary"
              size="large"
              icon={
                <ArrowTopRightOnSquareIcon
                  className="-ml-0.5 h-4 w-4"
                  aria-hidden="true"
                />
              }
              onClick={() =>
                window.open(
                  `https://${primaryDomain?.data().domain}/${
                    selectedDynamicPage?.data().path
                  }?utm=test_${websiteId}`,
                )
              }
            ></Button>
          </div>
        </div>
        <div
          className={classNames(
            'relative mx-auto rounded-[2.5rem] border-gray-800 bg-gray-800 transition-all',
            device === 'desktop' ? 'mt-4 h-full w-full' : '',
            device === 'mobile' ? 'mt-8 h-[780px] w-[390px] border-[14px]' : '',
            device === 'tablet'
              ? 'mt-8 h-[454px] max-w-[341px] border-[14px] md:h-[682px] md:max-w-[512px]'
              : '',
          )}
        >
          {device !== 'desktop' ? (
            <>
              <div className="absolute -left-[17px] top-[72px] h-[32px] w-[3px] rounded-l-lg bg-gray-800"></div>
              <div className="absolute -left-[17px] top-[124px] h-[46px] w-[3px] rounded-l-lg bg-gray-800"></div>
              <div className="absolute -left-[17px] top-[178px] h-[46px] w-[3px] rounded-l-lg bg-gray-800"></div>
              <div className="absolute -right-[17px] top-[142px] h-[64px] w-[3px] rounded-r-lg bg-gray-800"></div>
            </>
          ) : (
            <></>
          )}
          <div
            className={classNames(
              'h-full w-full overflow-hidden bg-white',
              device !== 'desktop' ? 'rounded-[2rem]' : '',
            )}
          >
            <WebsitePreview websiteId={websiteId} iframeRef={iframeRef} />
          </div>
        </div>
      </div>
    </FullScreenModal>
  );
}
